import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiArrowDown, FiArrowUpRight, FiCheck, FiChevronDown, FiCode, FiCommand, FiGlobe, FiLayers, FiPlay, FiPlus, FiSmartphone, FiZap } from 'react-icons/fi';
import HomeHeader, { HomeBrand } from '../components/home/HomeHeader';
import DashboardPreview from '../components/home/DashboardPreview';
import ProductShowcase, { products } from '../components/home/ProductShowcase';
import HomePricing from '../components/home/HomePricing';
import './Home.css';

const questions = [
  ['What is AppActs?', 'AppActs brings legal documents, smart links, customer support, internal ads and landing pages into one workspace. It helps you manage the practical side of running your apps and websites.'],
  ['Can I start with a free account?', 'Yes. You can create a free account to get started. The pricing section above shows the current limits for each plan, so you can choose what fits your project.'],
  ['Is this just for mobile apps?', 'No. You can manage web, iOS and Android projects. Smart links can direct visitors to a different destination depending on their platform.'],
  ['Do I need all five tools?', 'Use the tools your project needs. You can start with a legal document or a smart link, then add support, promotions or a landing page as your product grows.'],
];

function HomeFaq() {
  const [expanded, setExpanded] = useState(null);
  return <section className="home-section home-faq home-container" aria-labelledby="home-faq-title">
    <div className="home-reveal"><span className="home-eyebrow">A FEW GOOD QUESTIONS</span><h2 id="home-faq-title">Curious?<br />We've got you.</h2><p>Have something else in mind?</p><Link className="home-text-link" to="/contact">Let's talk <FiArrowUpRight aria-hidden="true" /></Link></div>
    <div className="home-faq-list">{questions.map(([question, answer], index) => <div className={`home-faq-item ${expanded === index ? 'is-open' : ''}`} key={question}>
      <h3><button type="button" id={`home-question-${index}`} aria-expanded={expanded === index} aria-controls={`home-answer-${index}`} onClick={() => setExpanded(expanded === index ? null : index)}>{question}<FiPlus aria-hidden="true" /></button></h3>
      <div className="home-faq-answer" id={`home-answer-${index}`} role="region" aria-labelledby={`home-question-${index}`} aria-hidden={expanded !== index}><div><p>{answer}</p></div></div>
    </div>)}</div>
  </section>;
}

function HomeFooter() {
  return <footer className="home-footer"><div className="home-container">
    <div className="home-footer-top"><div><HomeBrand /><p>Big ideas. Less busywork.</p></div><nav aria-label="Footer navigation"><a href="#products">Products</a><a href="#pricing">Pricing</a><Link to="/about">About</Link><Link to="/contact">Contact</Link></nav><a className="home-back-top" href="#home">Back to top <FiArrowUpRight aria-hidden="true" /></a></div>
    <div className="home-footer-bottom"><span>© {new Date().getFullYear()} AppActs. All rights reserved.</span><span>Made for the things you're going to build.</span></div>
  </div></footer>;
}

export default function Home({ user }) {
  const pageRef = useRef(null);
  const location = useLocation();
  const destination = user ? '/dashboard' : '/register';

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motion.matches || !('IntersectionObserver' in window)) return undefined;
    const nodes = pageRef.current.querySelectorAll('.home-reveal');
    // Visible content stays readable immediately; only offscreen content is revealed on scroll.
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.remove('home-reveal-pending');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    nodes.forEach(node => {
      if (node.getBoundingClientRect().top > window.innerHeight) {
        node.classList.add('home-reveal-pending');
        observer.observe(node);
      }
    });
    const showAll = () => {
      if (motion.matches) {
        nodes.forEach(node => node.classList.remove('home-reveal-pending'));
        observer.disconnect();
      }
    };
    motion.addEventListener('change', showAll);
    return () => { observer.disconnect(); motion.removeEventListener('change', showAll); nodes.forEach(node => node.classList.remove('home-reveal-pending')); };
  }, []);

  useEffect(() => {
    if (!location.hash) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const id = location.hash.slice(1);
      const target = products.some(product => product.id === id) ? 'products' : id;
      document.getElementById(target)?.scrollIntoView();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.hash]);

  return (
    <div className="home-page" id="home" ref={pageRef}>
      <a className="home-skip-link" href="#home-content">Skip to content</a>
      <HomeHeader user={user} />
      <main id="home-content" tabIndex={-1}>
        <section className="home-hero" aria-labelledby="home-title">
          <div className="home-hero-grid" aria-hidden="true" />
          <div className="home-container home-hero-layout">
            <div className="home-hero-copy">
              <a href="#products" className="home-hero-badge"><span /> YOUR NEXT IDEA STARTS HERE <FiArrowUpRight aria-hidden="true" /></a>
              <h1 id="home-title">You build.<br />We handle<br /><span>the rest.</span><span className="home-title-spark" aria-hidden="true">✳</span></h1>
              <p>The behind-the-scenes toolkit for your next big thing. Legal docs, smart links, support and more — all in one happy workspace.</p>
              <div className="home-hero-actions"><Link to={destination} className="home-button">{user ? 'Open your workspace' : 'Start building for free'}<FiArrowUpRight aria-hidden="true" /></Link><a className="home-button home-button-quiet" href="#products">Explore the toolkit <FiArrowDown aria-hidden="true" /></a></div>
              <div className="home-hero-note"><span><FiCheck aria-hidden="true" /> Free plan available</span><span><FiCheck aria-hidden="true" /> Built for independent builders</span></div>
            </div>
            <DashboardPreview />
          </div>
          <div className="home-container home-platform-strip"><span>FROM SIDE PROJECT TO WHAT'S NEXT.</span><div><span><FiGlobe aria-hidden="true" /> Web</span><span><FiSmartphone aria-hidden="true" /> iOS & Android</span><span><FiCode aria-hidden="true" /> Made for vibe coders</span></div><a href="#products" aria-label="Discover the products"><FiArrowDown /></a></div>
        </section>
        <ProductShowcase destination={destination} />
        <section id="how-it-works" className="home-section home-workflow">
          <div className="home-container">
            <div className="home-workflow-heading home-reveal"><div><span className="home-eyebrow">02 / LESS FRICTION. MORE MOMENTUM.</span><h2>From “what if”<br />to out in the world.</h2></div><p>You bring the idea. We bring the essentials.<br />A simpler way to get your project moving.</p></div>
            <div className="home-steps">
              {[
                [FiPlus, '01', 'Make yourself at home.', 'Create your account and add a project. A dedicated space for everything your app needs.'],
                [FiLayers, '02', 'Connect the essentials.', 'Create your documents, set up your links and give your users a way to reach you.'],
                [FiZap, '03', 'Keep building great things.', 'Share your pages, follow your analytics and manage your growing project from one place.'],
              ].map(([Icon, number, title, description]) => <article className="home-step home-reveal" key={number}><div className="home-step-top"><span><Icon aria-hidden="true" /></span><small>{number}</small></div><h3>{title}</h3><p>{description}</p></article>)}
            </div>
            <div className="home-workflow-footnote"><FiCommand aria-hidden="true" /><span>Less tab switching. More of the work you love.</span><span className="home-workflow-line" /></div>
          </div>
        </section>
        <HomePricing user={user} />
        <section id="docs" className="home-resources home-container home-reveal" aria-labelledby="home-resources-title"><div><span className="home-resource-icon"><FiPlay aria-hidden="true" /></span><div><span className="home-eyebrow">A LITTLE GUIDANCE GOES A LONG WAY</span><h2 id="home-resources-title">Your first steps, made simpler.</h2><p>See the tools in action with step-by-step video walkthroughs.</p></div></div><a href="#products" className="home-button home-button-outline">Explore walkthroughs <FiArrowUpRight aria-hidden="true" /></a></section>
        <HomeFaq />
        <section className="home-final-cta"><div className="home-container home-reveal"><span className="home-eyebrow"><span className="home-status-light" /> YOUR NEXT CHAPTER</span><h2>Less busywork.<br /><span>More “I built that.”</span></h2><p>That idea you've been thinking about?<br />Let's make room for it.</p><Link to={destination} className="home-button">{user ? 'Back to your workspace' : 'Let’s build something'}<FiArrowUpRight aria-hidden="true" /></Link><a className="home-final-secondary" href="#pricing">Find your plan <FiChevronDown aria-hidden="true" /></a></div><span className="home-cta-star" aria-hidden="true">✳</span></section>
      </main>
      <HomeFooter />
    </div>
  );
}
